
import { Comment } from './types/comment';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { Lock } from 'lucide-react';

interface CommentsListProps {
  comments: Comment[];
  selectedCommentId: string | null;
  onSelectComment: (commentId: string) => void;
}

const CommentsList = ({ comments, selectedCommentId, onSelectComment }: CommentsListProps) => {
  const { isSubscriptionActive, isLoading } = useSubscriptionStatus();

  // Helper function to get sentiment style
  const getSentimentStyle = (sentiment?: string) => {
    switch(sentiment) {
      case 'positive':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
      case 'negative':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  // Helper function to get sentiment label in French
  const getSentimentLabel = (sentiment?: string) => {
    switch(sentiment) {
      case 'positive': return 'Positif';
      case 'negative': return 'Négatif';
      default: return 'Neutre';
    }
  };

  return (
    <div className="overflow-y-auto flex-1">
      {comments.map((comment) => (
        <div
          key={comment.id}
          className={`p-4 border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors ${
            selectedCommentId === comment.id ? 'bg-custom-primaryLight dark:bg-custom-primary/20' : ''
          } ${!comment.isRead ? 'font-semibold' : ''}`}
          onClick={() => onSelectComment(comment.id)}
        >
          <div className="flex items-start">
            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-800 dark:text-gray-200 text-sm">
              {comment.author.name.charAt(0) || 'U'}
            </div>
            <div className="ml-3 flex-1">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {comment.author.name || 'Utilisateur inconnu'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(comment.timestamp).toLocaleDateString()}
                </span>
              </div>
              
              {/* Apply subscription lock overlay to comment content */}
              <div className="relative">
                <p className={`text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mt-1 ${(!isSubscriptionActive && !isLoading) ? 'blur-sm' : ''}`}>
                  {comment.content}
                </p>
                
                {/* Overlay lock icon if subscription is not active and not loading */}
                {!isSubscriptionActive && !isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="mt-2 flex space-x-2">
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {comment.platform}
                </span>
                
                {/* Make sentiment badge more prominent */}
                <span className={`px-2 py-1 text-xs rounded-full ${getSentimentStyle(comment.sentimentLabel)}`}>
                  {getSentimentLabel(comment.sentimentLabel)}
                </span>
                
                {!comment.isRead && (
                  <span className="px-2 py-1 text-xs rounded-full bg-custom-primaryLight dark:bg-custom-primary/30 text-custom-primary dark:text-custom-secondaryLight">
                    Nouveau
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CommentsList;
